import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import BlockIcon from '@mui/icons-material/Block';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import { API_URL } from '../../config';

// Entity types this panel can review. Add a new entry when the registry
// onboards a new type (universities, degree programs, etc.).
const ENTITY_TYPES = [
  { key: 'companies', label: 'Companies', singular: 'company' },
];

const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(diff / 3600000);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(diff / 86400000);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
};

// Single dialog with three modes — keeps inputs co-located and the action
// surface narrow. Each mode has its own field set + submit verb.
const ActionDialog = ({ mode, stub, entityType, onClose, onDone }) => {
  // mode: 'approve' | 'merge' | 'reject' | null
  const [domain, setDomain] = useState('');
  const [extraAliases, setExtraAliases] = useState('');
  const [targetSlug, setTargetSlug] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset fields whenever a new stub is opened.
  useEffect(() => {
    if (stub) {
      // Sensible default: most company names slug to {name}.com
      setDomain(stub.slug ? `${stub.slug}.com` : '');
      setExtraAliases('');
      setTargetSlug('');
      setReason('');
      setError(null);
    }
  }, [stub, mode]);

  if (!mode || !stub) return null;

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    const base = `${API_URL}/api/admin/entities/${entityType}/${stub.slug}`;
    try {
      if (mode === 'approve') {
        if (!domain.trim()) {
          setError('Domain is required');
          setSubmitting(false);
          return;
        }
        const aliases = extraAliases
          .split(',')
          .map((a) => a.trim())
          .filter(Boolean);
        await axios.post(`${base}/approve`, {
          domain: domain.trim(),
          extra_aliases: aliases.length ? aliases : null,
        });
        onDone({ severity: 'success', text: `Approved ${stub.name} → ${domain.trim()}` });
      } else if (mode === 'merge') {
        if (!targetSlug.trim()) {
          setError('Target slug is required');
          setSubmitting(false);
          return;
        }
        await axios.post(`${base}/merge`, { target_slug: targetSlug.trim() });
        onDone({ severity: 'success', text: `Merged ${stub.name} → ${targetSlug.trim()}` });
      } else if (mode === 'reject') {
        await axios.post(`${base}/reject`, { reason: reason.trim() || null });
        onDone({ severity: 'success', text: `Rejected ${stub.name}` });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const titles = {
    approve: `Approve "${stub.name}"`,
    merge: `Merge "${stub.name}" into existing`,
    reject: `Reject "${stub.name}"`,
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{titles[mode]}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Pending slug: <code>{stub.slug}</code>
            {' · '}seen {stub.occurrences || 1}×
            {stub.added_by_user ? ` · added by ${stub.added_by_user}` : ''}
          </Typography>

          {mode === 'approve' && (
            <>
              <TextField
                label="Domain"
                helperText="The canonical id for this entity (e.g. stripe.com). Used as the storage slug."
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                fullWidth
                autoFocus
              />
              <TextField
                label="Extra aliases (comma-separated, optional)"
                helperText="Other forms users might type. The stub's name is added automatically."
                value={extraAliases}
                onChange={(e) => setExtraAliases(e.target.value)}
                fullWidth
              />
            </>
          )}

          {mode === 'merge' && (
            <TextField
              label="Target slug"
              helperText="The approved entity to merge into (its existing storage slug, usually a domain)."
              value={targetSlug}
              onChange={(e) => setTargetSlug(e.target.value)}
              fullWidth
              autoFocus
            />
          )}

          {mode === 'reject' && (
            <TextField
              label="Reason (optional)"
              helperText="Logged with the rejection. The stub is deleted; if the same text appears later, it'll create a fresh stub."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              multiline
              rows={2}
              autoFocus
            />
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          onClick={submit}
          variant="contained"
          color={mode === 'reject' ? 'error' : 'primary'}
          disabled={submitting}
        >
          {submitting ? 'Working…' : mode.charAt(0).toUpperCase() + mode.slice(1)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EntitiesPanel = () => {
  const [entityType, setEntityType] = useState(ENTITY_TYPES[0].key);
  const [pending, setPending] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [dialog, setDialog] = useState({ mode: null, stub: null });

  const fetchPending = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/admin/entities/${entityType}/pending`,
      );
      setPending(data.pending || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [entityType]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const rebuild = async () => {
    setRebuilding(true);
    setFlash(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/entities/${entityType}/rebuild-aliases`,
      );
      const collisionMsg = data.collisions?.length
        ? ` · ${data.collisions.length} collision(s)`
        : '';
      setFlash({
        severity: 'success',
        text: `Rebuilt aliases index: ${data.entries} entries from ${data.records_seen} records${collisionMsg}`,
      });
    } catch (e) {
      setFlash({
        severity: 'error',
        text: `Rebuild failed: ${e?.response?.data?.detail || e.message}`,
      });
    } finally {
      setRebuilding(false);
    }
  };

  const handleActionDone = (msg) => {
    setFlash(msg);
    setDialog({ mode: null, stub: null });
    fetchPending();
  };

  const typeMeta = ENTITY_TYPES.find((t) => t.key === entityType);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="flex-start" sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Pending {typeMeta?.label.toLowerCase()} awaiting review. Auto-created when a
            free-text input (resume parse, manual experience save, etc.) doesn't resolve
            to a known {typeMeta?.singular}. Sorted by occurrence count.
          </Typography>
        </Box>
        <Tooltip title="Rebuild the alias index from all approved records. Run after a batch of approvals or a fresh enrichment pass.">
          <span>
            <Button
              startIcon={<RefreshIcon />}
              onClick={rebuild}
              disabled={rebuilding}
              size="small"
              variant="outlined"
            >
              {rebuilding ? 'Rebuilding…' : 'Rebuild aliases index'}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {ENTITY_TYPES.length > 1 && (
        <Tabs
          value={entityType}
          onChange={(_, v) => setEntityType(v)}
          sx={{ mb: 2 }}
        >
          {ENTITY_TYPES.map((t) => (
            <Tab key={t.key} value={t.key} label={t.label} />
          ))}
        </Tabs>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {flash && (
        <Alert severity={flash.severity} sx={{ mb: 2 }} onClose={() => setFlash(null)}>
          {flash.text}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : pending.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">
            No pending {typeMeta?.label.toLowerCase()}. New stubs appear here as users
            save profiles with unrecognized {typeMeta?.singular} names.
          </Typography>
        </Box>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {total} pending
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell align="right">Seen</TableCell>
                <TableCell>Added by</TableCell>
                <TableCell>Last seen</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pending.map((stub) => (
                <TableRow key={stub.slug} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {stub.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {stub.slug}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${stub.occurrences || 1}×`}
                      size="small"
                      color={stub.occurrences > 1 ? 'primary' : 'default'}
                      variant={stub.occurrences > 1 ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {stub.added_by_user || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={stub.last_seen_at || ''}>
                      <Typography variant="caption" color="text.secondary">
                        {fmtTime(stub.last_seen_at)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Approve as new entity">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => setDialog({ mode: 'approve', stub })}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Merge into existing entity">
                      <IconButton
                        size="small"
                        onClick={() => setDialog({ mode: 'merge', stub })}
                      >
                        <CallMergeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reject (delete stub)">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDialog({ mode: 'reject', stub })}
                      >
                        <BlockIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      <ActionDialog
        mode={dialog.mode}
        stub={dialog.stub}
        entityType={entityType}
        onClose={() => setDialog({ mode: null, stub: null })}
        onDone={handleActionDone}
      />
    </Paper>
  );
};

export default EntitiesPanel;
